﻿import '@/styles/navBarStyles.scss';
import { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import CapTracLogo from '@/components/CapTracLogo';
import UserAvatarSVG from '@/components/UserAvatarSVG'
import ActionItemBadge from '@/components/ActionItemBadge';
import { IoCheckmarkCircle, IoWarning } from 'react-icons/io5';
import * as Sentry from '@sentry/react';

// Configurable constants
const SUBMENU_CLOSE_DELAY_MS = 1000;

// Centralized menu structure (main, user, settings)
// Updated: Added nested "Dashboards" submenu for all process menus (Finance, Leasing, Legal, Marketing & CSI, IT)
const MENU_STRUCTURE = {
    main: [
        { label: 'Executive Dashboard', to: '/dashboards/executive-dash', pageRoute: '/dashboards/executive-dash' },
    {
      label: 'Operations', processName: 'Operations',
        dropdown: [
            {
                label: 'Dashboards',
                dropdown: [
                    { label: 'Operations Dashboard', to: '/dashboards/operations-dash', pageRoute: '/dashboards/operations-dash' },
                    { label: 'Footfall Dashboard', to: '/dashboards/footfall-dashboard', pageRoute: '/dashboards/footfall-dashboard' },
                    { label: 'Sustainability Dashboard', to: '/dashboards/sustainability-dashboard', pageRoute: '/dashboards/sustainability-dashboard' },
                ]
            },
            {
                label: 'Management',
                dropdown: [
                    { label: "SIPA", to: '/sipa/reports', pageRoute: '/sipa/reports' },
                    { label: 'Daily Checklist', to: '/operations/cm-daily-checklist', pageRoute: '/operations/cm-daily-checklist' },
                    { label: 'Tenant Non Compliance', to: '/operations/tenant-non-compliance-form', pageRoute: '/operations/tenant-non-compliance-form' },
                    { label: 'Asset Management', to: '/operations/asset-management', pageRoute: '/operations/asset-management' },
                    { label: 'Incidents', to: '/operations/incidents', pageRoute: '/operations/incidents' },
                    { label: 'Outstanding Certificates', to: '/operations/outstanding-certificates', pageRoute: '/operations/outstanding-certificates' },
                    { label: 'Management Hub', to: '/operations/management-hub', pageRoute: '/operations/management-hub' },
                ]
            },
      ],
    },
    {
      label: 'Finance', processName: 'Finance',
      dropdown: [
        {
          label: 'Dashboards',
          dropdown: [
            { label: 'Finance Dashboard', to: '/dashboards/finance-dashboard', pageRoute: '/dashboards/finance-dashboard' },
            { label: 'Arrears Dashboard', to: '/dashboards/arrears-dashboard', pageRoute: '/dashboards/arrears-dashboard' },
          ]
        },
      ],
    },
    {
      label: 'Leasing', processName: 'Leasing',
      dropdown: [
        {
          label: 'Dashboards',
          dropdown: [
            { label: 'Leasing Dashboard', to: '/dashboards/leasing-dashboard', pageRoute: '/dashboards/leasing-dashboard' },
            /*{ label: 'Turnover Dashboard', to: '/leasing/turnover-dashboard', pageRoute: '/leasing/turnover-dashboard' },*/
          ]
        },
      ],
    },
    {
      label: 'Legal', processName: 'Legal',
      dropdown: [
        {
          label: 'Dashboards',
          dropdown: [
            { label: 'Legal & Arrears Dashboard', to: '/dashboards/legal-and-arrears-dashboard', pageRoute: '/dashboards/legal-and-arrears-dashboard' }
          ]
        }
      ],
    },
    {
      label: 'Marketing & CSI', processName: 'Marketing',
      dropdown: [
        {
          label: 'Dashboards',
          dropdown: [
            { label: 'Media & Promotions Dashboard', to: '/dashboards/media-and-promotions-dashboard', pageRoute: '/dashboards/media-and-promotions-dashboard' },
            { label: 'CSI Dashboard', to: '/dashboards/csi-dashboard', pageRoute: '/dashboards/csi-dashboard' },
          ]
        },
        {
          label: 'Media',
          dropdown: [
            { label: 'Media Spaces', to: '/marketing/media-spaces', pageRoute: '/marketing/media-spaces' },
            { label: 'Media Bookings', to: '/marketing/media-bookings', pageRoute: '/marketing/media-bookings' },
          ]
        },
        {
          label: 'Promotions',
          dropdown: [
            { label: 'Promotional Areas', to: '/marketing/promotional-areas', pageRoute: '/marketing/promotional-areas' },
            { label: 'Promotional Bookings', to: '/marketing/promotional-bookings', pageRoute: '/marketing/promotional-bookings' },
          ]
        },
        {
          label: 'Management',
          dropdown: [
            { label: 'Counterparties', to: '/marketing/counterparties', pageRoute: '/marketing/counterparties' },
            { label: 'Contracts', to: '/marketing/contracts', pageRoute: '/marketing/contracts' },
            { label: 'No-Charge Approvals', to: '/marketing/no-charge-approvals', pageRoute: '/marketing/no-charge-approvals' },
          ]
        },
      ],
    },
    {
      label: 'IT', processName: 'IT',
      dropdown: [
        {
          label: 'Reporting',
          dropdown: [
            { label: 'IT Report', to: '/dashboards/itreport', pageRoute: '/dashboards/itreport' },
            { label: 'Threat Hunting Report', to: '/dashboards/threat-hunting', pageRoute: '/dashboards/threat-hunting' },
          ]
        },
        {
          label: 'Monitoring',
          dropdown: [
            { label: 'Connectivity', to: '/it/monitoring/connectivity', pageRoute: '/it/monitoring/connectivity' },
          ]
        },
        {
          label: 'Management',
          dropdown: [
            { label: 'Routers', to: '/it/config-management/routers', pageRoute: '/it/config-management/routers' },
            { label: 'Endpoint Management', to: '/it/endpoint-management', pageRoute: '/it/endpoint-management' },
          ]
        },
      ],
    }
  ],
  user: [
    { label: 'Profile', to: '/admin/user/profile', pageRoute: '/admin/user/profile' },
    { label: 'Action Items', to: '/admin/action-items', pageRoute: '/admin/action-items', hasBadge: true },
    { label: 'Projects', to: '/projects', pageRoute: '/projects' },
    { label: 'Settings', to: '/admin/settings', pageRoute: '/admin/settings' },
    { label: 'Log Out', action: 'logout' }
   ]
};
 
const UserMenu = ({
    mobile = false,
    userName = '',
    onDesktopMouseEnter,
    onDesktopMouseLeave,
    onMobileToggle,
    userMenuOpen = false,
    dropdownItems = null,
    noAutoMargin = false
}) => (
    <li
        className={`navbar-item navbar-user-menu${mobile ? ' mobile' : ''}`}
        style={mobile ? { position: 'static', marginLeft: 0, width: '100%', padding: 0 } : { marginLeft: noAutoMargin ? '4px' : 'auto', position: 'relative' }}
        onMouseEnter={!mobile ? onDesktopMouseEnter : undefined}
        onMouseLeave={!mobile ? onDesktopMouseLeave : undefined}
        onClick={mobile ? onMobileToggle : undefined}
    >
    <div className="user-avatar-name" style={mobile ? { display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '16px 32px', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : { display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      <span className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '10px', background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <UserAvatarSVG />
      </span>
      <span className="user-name" style={{ fontWeight: 700, color: 'var(--white)', fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</span>
    </div>
    {!mobile && userMenuOpen && dropdownItems && (
      <ul className="dropdown-menu user-dropdown-menu" style={{ right: 0, left: 'auto', minWidth: 180, top: '100%' }}>
        {dropdownItems}
      </ul>
    )}
  </li>
);

const NavBar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [nestedDropdownOpen, setNestedDropdownOpen] = useState(null); // desktop nested
  const [nestedMobileOpen, setNestedMobileOpen] = useState(null); // mobile nested
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [flyoutClosing, setFlyoutClosing] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [userFlyoutOpen, setUserFlyoutOpen] = useState(false);
  const [settingsMenuTimeoutId, setSettingsMenuTimeoutId] = useState(null);
  const [dropdownTimeoutId, setDropdownTimeoutId] = useState(null);
  const [nestedDropdownTimeoutId, setNestedDropdownTimeoutId] = useState(null);
  const { logout, user, permissions, hasPermission, apiFetch } = useAuth();
  const navRef = useRef(null);
  const flyoutTimeout = useRef();
  const navigate = useNavigate();
  const [monitoringStatuses, setMonitoringStatuses] = useState([]);
  const [showMonitoringTooltip, setShowMonitoringTooltip] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 950);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!burgerOpen) return;
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setFlyoutClosing(true);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [burgerOpen]);

  useEffect(() => {
    if (!flyoutClosing) return;
    flyoutTimeout.current = setTimeout(() => {
      setBurgerOpen(false);
      setActiveDropdown(null);
      setNestedDropdownOpen(null);
      setNestedMobileOpen(null);
      setUserMenuOpen(false);
      setFlyoutClosing(false);
    }, 320);
    return () => clearTimeout(flyoutTimeout.current);
  }, [flyoutClosing]);

  // Cleanup effect to clear all active timeouts when component unmounts
  // This prevents memory leaks and errors if timeouts fire after unmount
  useEffect(() => {
    return () => {
      if (settingsMenuTimeoutId) clearTimeout(settingsMenuTimeoutId);
      if (dropdownTimeoutId) clearTimeout(dropdownTimeoutId);
      if (nestedDropdownTimeoutId) clearTimeout(nestedDropdownTimeoutId);
    };
  }, [settingsMenuTimeoutId, dropdownTimeoutId, nestedDropdownTimeoutId]);

    // Poll the monitoring-status endpoint; silently skips for users without secrets access
    useEffect(() => {
        const fetchMonitoringStatus = async () => {
            try {
                const res = await apiFetch('/api/AppSecrets/monitoring-status');
                if (res && res.ok) {
                    const data = await res.json();
                    if (data.success) setMonitoringStatuses(data.data ?? []);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Non-critical — badge simply won't render
            }
        };
        fetchMonitoringStatus();
        const interval = setInterval(fetchMonitoringStatus, 60_000);
        return () => clearInterval(interval);
    }, [apiFetch]);

  const handleDropdown = (id) => {
    // Clear any pending close timeout when entering a menu
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId);
      setDropdownTimeoutId(null);
    }
    if (nestedDropdownTimeoutId) {
      clearTimeout(nestedDropdownTimeoutId);
      setNestedDropdownTimeoutId(null);
    }

    if (isMobile) {
      // Close user flyouts when switching to a main dropdown
      setUserFlyoutOpen(false);
      // Toggle active menu
      setActiveDropdown(activeDropdown === id ? null : id);
      // Reset nested submenu only when changing to a different top-level
      if (activeDropdown !== id) {
        setNestedMobileOpen(null);
      }
    } else {
      setActiveDropdown(id);
      setNestedDropdownOpen(null);
    }
  };

  // Helper for mobile nested submenu toggle (prevents accidental propagation)
  const toggleNestedMobile = (label, e) => {
    if (e) e.stopPropagation();
    setNestedMobileOpen(prev => prev === label ? null : label);
  };

  const handleDropdownLeave = () => {
    if (!isMobile) {
      // Clear any existing timeout
      if (dropdownTimeoutId) {
        clearTimeout(dropdownTimeoutId);
      }
      // Set a delayed close
      const timeoutId = setTimeout(() => {
        setActiveDropdown(null);
        setNestedDropdownOpen(null);
        setDropdownTimeoutId(null);
      }, SUBMENU_CLOSE_DELAY_MS);
      setDropdownTimeoutId(timeoutId);
    }
  };

  const handleNestedDropdownEnter = (label) => {
    // Clear any pending close timeouts when entering nested menu
    if (nestedDropdownTimeoutId) {
      clearTimeout(nestedDropdownTimeoutId);
      setNestedDropdownTimeoutId(null);
    }
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId);
      setDropdownTimeoutId(null);
    }
    setNestedDropdownOpen(label);
  };

  const handleNestedDropdownLeave = () => {
    // Clear any existing timeout
    if (nestedDropdownTimeoutId) {
      clearTimeout(nestedDropdownTimeoutId);
    }
    // Set a delayed close for nested dropdown
    const timeoutId = setTimeout(() => {
      setNestedDropdownOpen(null);
      setNestedDropdownTimeoutId(null);
    }, SUBMENU_CLOSE_DELAY_MS);
    setNestedDropdownTimeoutId(timeoutId);
  };

  const handleDropdownItemEnter = () => {
    // Clear any pending close timeout when entering a dropdown item
    if (dropdownTimeoutId) {
      clearTimeout(dropdownTimeoutId);
      setDropdownTimeoutId(null);
    }
  };
 
  const clearSettingsMenuTimeout = () => {
    if (settingsMenuTimeoutId) {
      clearTimeout(settingsMenuTimeoutId);
      setSettingsMenuTimeoutId(null);
    }
  };
 
  const handleSettingsMenuMouseEnter = () => {
    clearSettingsMenuTimeout();
    setSettingsMenuOpen(true);
  };
  const handleSettingsMenuMouseLeave = () => {
    clearSettingsMenuTimeout();
    const timeoutId = setTimeout(() => {
      setSettingsMenuOpen(false);
      setSettingsMenuTimeoutId(null);
    }, SUBMENU_CLOSE_DELAY_MS);
    setSettingsMenuTimeoutId(timeoutId);
  };

  const handleUserMenuMouseEnter = () => {
    clearSettingsMenuTimeout();
    setUserMenuOpen(true);
  };
  const handleUserMenuMouseLeave = () => {
     setUserMenuOpen(false);
     // Start delayed close for settings submenu instead of immediate close
     handleSettingsMenuMouseLeave();
    };

    const handleNavClick = (e, to) => {
        e.preventDefault();
        //console.log(`[Nav] navigating → ${to}`);
        navigate(to);
    };

  const hasProcessPermission = (processName) =>
    permissions?.some(p => (p.processName || '').toLowerCase().startsWith((processName || '').toLowerCase()));
  const hasPageRoutePermission = (pageRoute) =>
    permissions?.some(p => p.pageRoute === pageRoute);

  const renderMenu = ({ items, mobile = false, userMenu = false, onDropdown = null, onDropdownLeave = null, onSettingsMenuEnter = null, onSettingsMenuLeave = null }) => (
    items
      .filter(item => {
        if (!userMenu && item.dropdown) {
          // show parent if any child permitted
          return item.dropdown.some(sub => {
            if (sub.dropdown) {
              return sub.dropdown.some(n => hasPageRoutePermission(n.pageRoute));
            }
            return hasPageRoutePermission(sub.pageRoute);
          });
        }
        if (!userMenu && item.processName) {
          return hasProcessPermission(item.processName);
        }
        // For items with pageRoute (both main and user menu), check permission
        if (item.pageRoute) {
          return hasPageRoutePermission(item.pageRoute);
        }
        // Items without pageRoute (like logout) always show
        return true;
      })
      .map(item => {
        if (item.dropdown) {
          const visibleDropdown = userMenu
            ? item.dropdown
            : item.dropdown.filter(sub => {
                if (sub.dropdown) {
                  return sub.dropdown.some(n => hasPageRoutePermission(n.pageRoute));
                }
                return hasPageRoutePermission(sub.pageRoute);
              });
          if (visibleDropdown.length === 0) return null;

          if (userMenu) {
            return (
              <li
                key={item.label}
                className={`dropdown-item settings-submenu${settingsMenuOpen ? ' open' : ''}`}
                onMouseEnter={onSettingsMenuEnter}
                onMouseLeave={onSettingsMenuLeave}
                style={{ position: 'relative' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {item.label}
                  <span style={{ marginLeft: 8 }}>▶</span>
                </span>
                {settingsMenuOpen && (
                  <ul
                    className="dropdown-menu settings-dropdown-menu"
                    style={{ position: 'absolute', right: '100%', left: 'auto', top: 0, minWidth: 200, zIndex: 10 }}
                    onMouseEnter={onSettingsMenuEnter}
                    onMouseLeave={onSettingsMenuLeave}
                  >
                    {visibleDropdown.map(sub => (
                      <li className="dropdown-item" key={sub.to}>
                         <a href={sub.to} onClick={(e) => { handleNavClick(e, sub.to); setBurgerOpen(false); setUserMenuOpen(false); setSettingsMenuOpen(false); }}>{sub.label}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          }

          return (
            <li
              key={item.label}
              className={`navbar-item${activeDropdown === item.label.toLowerCase() ? ' active' : ''}`}
              onMouseEnter={!mobile ? () => onDropdown(item.label.toLowerCase()) : undefined}
              onMouseLeave={!mobile ? onDropdownLeave : undefined}
              onClick={mobile ? () => onDropdown(item.label.toLowerCase()) : undefined}
            >
              <span className="dropdown-toggle">{item.label}</span>
              {!mobile && (activeDropdown === item.label.toLowerCase()) && (
                <ul 
                  className="dropdown-menu"
                  onMouseEnter={() => onDropdown(item.label.toLowerCase())}
                  onMouseLeave={onDropdownLeave}
                >
                  {visibleDropdown.map(sub => {
                    // Nested submenu (Dashboards)
                    if (sub.dropdown) {
                      const nestedVisible = sub.dropdown.filter(n => hasPageRoutePermission(n.pageRoute));
                      if (nestedVisible.length === 0) return null;
                      return (
                        <li
                          key={sub.label}
                          className={`dropdown-item nested-submenu${nestedDropdownOpen === sub.label.toLowerCase() ? ' open' : ''}`}
                          onMouseEnter={() => handleNestedDropdownEnter(sub.label.toLowerCase())}
                          onMouseLeave={handleNestedDropdownLeave}
                          style={{ position: 'relative' }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {sub.label}
                            <span style={{ marginLeft: 8 }}>▶</span>
                          </span>
                          {nestedDropdownOpen === sub.label.toLowerCase() && (
                            <ul
                              className="dropdown-menu nested-dropdown-menu"
                              style={{ position: 'absolute', left: '100%', top: 0, minWidth: 220, zIndex: 15 }}
                              onMouseEnter={() => handleNestedDropdownEnter(sub.label.toLowerCase())}
                              onMouseLeave={handleNestedDropdownLeave}
                            >
                              {nestedVisible.map(n => (
                                <li className="dropdown-item" key={n.to}>
                                      <a href={n.to} onClick={(e) => { handleNavClick(e, n.to); setBurgerOpen(false); setActiveDropdown(null); setNestedDropdownOpen(null); }}>{n.label}</a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    }
                    // Regular item
                    return (
                      <li 
                        className="dropdown-item" 
                        key={sub.to}
                        onMouseEnter={handleDropdownItemEnter}
                      >
                         <a href={sub.to} onClick={(e) => { handleNavClick(e, sub.to); setBurgerOpen(false); }}>{sub.label}</a>
                      </li>
                    );
                  })}
                </ul>
              )}
              {mobile && activeDropdown === item.label.toLowerCase() && (
                <div className="mobile-flyout-submenu">
                  <ul>
                    {visibleDropdown.map(sub => {
                      if (sub.dropdown) {
                        const nestedVisible = sub.dropdown.filter(n => hasPageRoutePermission(n.pageRoute));
                        if (nestedVisible.length === 0) return null;
                        const labelKey = sub.label.toLowerCase();
                        const open = nestedMobileOpen === labelKey;
                        return (
                          <li key={sub.label} className="dropdown-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <button
                              type="button"
                              style={{ display: 'flex', width: '100%', justifyContent: 'space-between', cursor: 'pointer', background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit' }}
                              onClick={(e) => toggleNestedMobile(labelKey, e)}
                              aria-expanded={open}
                            >
                              {sub.label} <span>{open ? '▼' : '▶'}</span>
                            </button>
                            {open && (
                              <ul style={{ marginTop: 8 }}>
                                {nestedVisible.map(n => (
                                  <li className="dropdown-item" key={n.to}>
                                        <a href={n.to} onClick={(e) => { handleNavClick(e, n.to); setBurgerOpen(false); setActiveDropdown(null); setNestedMobileOpen(null); }}>{n.label}</a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      }
                      return (
                        <li className="dropdown-item" key={sub.to}>
                              <a href={sub.to} onClick={(e) => { handleNavClick(e, sub.to); setBurgerOpen(false); setActiveDropdown(null); }}>{sub.label}</a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </li>
          );
        }

        if (userMenu && item.action === 'logout') {
          return (
            <li className="dropdown-item" key={item.label} onClick={() => logout()}>
              <span style={{ cursor: 'pointer' }}>{item.label}</span>
            </li>
          );
        }

        if (userMenu) {
          return (
            <li className="dropdown-item" key={item.label}>
                  <a href={item.to} onClick={(e) => { handleNavClick(e, item.to); setBurgerOpen(false); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      {item.label}
                      {item.hasBadge && <ActionItemBadge />}
                  </a>
            </li>
          );
        }

        return (
          <li className="navbar-item" key={item.label}>
                <a href={item.to || '#'} onClick={(e) => { handleNavClick(e, item.to || '#'); setBurgerOpen(false); }}>{item.label}</a>
          </li>
        );
      })
  );

  const renderMobileFlyoutSubmenu = () => {
    // Handled inside renderMenu after refactor
    return null;
  };

  const renderMobileUserFlyout = () => {
    if (!isMobile || !userFlyoutOpen) return null;
    return (
      <div className={`mobile-flyout-submenu${flyoutClosing ? ' closing' : ''}`}>
        <ul>
          {hasPageRoutePermission('/admin/user/profile') && (
            <li className="dropdown-item">
                        <a href="/admin/user/profile" onClick={(e) => { handleNavClick(e, '/admin/user/profile'); setBurgerOpen(false); setUserFlyoutOpen(false); }}>Profile</a>
            </li>
          )}
          {hasPageRoutePermission('/admin/action-items') && (
            <li className="dropdown-item">
                <a href="/admin/action-items" onClick={(e) => { handleNavClick(e, '/admin/action-items'); setBurgerOpen(false); setUserFlyoutOpen(false); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    Action Items
                    <ActionItemBadge />
                </a>
            </li>
          )}
          {hasPageRoutePermission('/projects') && (
            <li className="dropdown-item">
                        <a href="/projects" onClick={(e) => { handleNavClick(e, '/projects'); setBurgerOpen(false); setUserFlyoutOpen(false); }}>Projects</a>
            </li>
          )}
          {hasPageRoutePermission('/admin/settings') && (
            <li className="dropdown-item">
                        <a href="/admin/settings" onClick={(e) => { handleNavClick(e, '/admin/settings'); setBurgerOpen(false); setUserFlyoutOpen(false); }}>Settings</a>
            </li>
          )}
          <li className="dropdown-item" onClick={() => { setBurgerOpen(false); setUserFlyoutOpen(false); logout(); }}>
            <span style={{ cursor: 'pointer' }}>Log Out</span>
          </li>
        </ul>
      </div>
    );
  };

  const userDisplayName = (user ? `${user.FirstName || user.firstName || ''} ${user.LastName || user.lastName || ''}` : '').trim();
  const desktopUserDropdownItems = renderMenu({
    items: MENU_STRUCTURE.user,
    userMenu: true,
    onSettingsMenuEnter: handleSettingsMenuMouseEnter,
    onSettingsMenuLeave: handleSettingsMenuMouseLeave
  });

  const getLandingPage = () => {
    const userHomeDashboard = user?.HomeDashboardRoute || user?.homeDashboardRoute;
    if (userHomeDashboard && hasPermission(userHomeDashboard)) {
      return userHomeDashboard;
    }
    if (hasPermission('/dashboards/executive-dash')) return '/dashboards/executive-dash';
    if (hasPermission('/operations/centremanager/home')) return '/operations/centremanager/home';
    return '/home';
  };

    return (
        <nav className="navbar" ref={navRef}>
            {/* Desktop logo (left) */}
            <div
                className="navbar-logo"
                style={{
                    display: isMobile ? 'none' : 'flex',
                    alignItems: 'center',
                    height: '100%',
                    cursor: 'pointer'
                }}
                onClick={() => window.location.assign(getLandingPage())}
                role="button"
                tabIndex={0}
                aria-label="Go to home"
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') window.location.assign(getLandingPage());
                }}
            >
                <CapTracLogo width={180} height={58} />
            </div>
            {/* Desktop menu */}
            <ul className="navbar-menu" style={isMobile ? { display: 'none' } : {}}>
                {renderMenu({
                    items: MENU_STRUCTURE.main,
                    mobile: false,
                    onDropdown: handleDropdown,
                    onDropdownLeave: handleDropdownLeave
                })}
                {/* System health badge — green ✓ all pass, orange ⚠ any fail */}
                {monitoringStatuses.length > 0 && (
                    <li
                        className="navbar-item monitoring-badge-item"
                        style={{ marginLeft: 'auto' }}
                        onMouseEnter={() => setShowMonitoringTooltip(true)}
                        onMouseLeave={() => setShowMonitoringTooltip(false)}
                    >
                        {monitoringStatuses.every(s => s.lastStatus === true)
                            ? <span className="monitoring-check-wrapper"><IoCheckmarkCircle size={22} color="#28a745" /></span>
                            : <IoWarning size={22} color="#e67700" />
                        }
                        {showMonitoringTooltip && (
                            <div className="monitoring-status-tooltip">
                                <div className="monitoring-tooltip-title">System Status</div>
                                {monitoringStatuses.map(s => (
                                    <div key={s.secretKey} className="monitoring-tooltip-row">
                                        <span className={`monitoring-dot ${s.lastStatus === true ? 'pass' :
                                                s.lastStatus === false ? 'fail' : 'unknown'
                                            }`} />
                                        <span className="monitoring-name">{s.displayName || s.secretKey}</span>
                                        <span className={`monitoring-status-text ${s.lastStatus === true ? 'pass' :
                                                s.lastStatus === false ? 'fail' : 'unknown'
                                            }`}>
                                            {s.lastStatus === true ? '✓' : s.lastStatus === false ? '✗' : '—'}
                                        </span>
                                    </div>
                                ))}
                                {monitoringStatuses.some(s => s.lastCheckedAt) && (
                                    <div className="monitoring-tooltip-footer">
                                        Last checked:{' '}
                                        {new Date(
                                            [...monitoringStatuses]
                                                .filter(s => s.lastCheckedAt)
                                                .sort((a, b) => new Date(b.lastCheckedAt) - new Date(a.lastCheckedAt))[0]
                                                ?.lastCheckedAt
                                        ).toLocaleTimeString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </li>
                )}
                <UserMenu
                    userName={userDisplayName}
                    onDesktopMouseEnter={handleUserMenuMouseEnter}
                    onDesktopMouseLeave={handleUserMenuMouseLeave}
                    userMenuOpen={userMenuOpen}
                    dropdownItems={desktopUserDropdownItems}
                    noAutoMargin={monitoringStatuses.length > 0}
                />
            </ul>
            {/* Mobile: CapTracLogo as menu button, right-aligned */}
            {isMobile && (
                <button
                    className="mobile-logo-menu-btn"
                    aria-label="Open menu"
                    onClick={() => {
                        if (burgerOpen) {
                            setFlyoutClosing(true);
                        } else {
                            setBurgerOpen(true);
                            setActiveDropdown(null);
                            setNestedMobileOpen(null);
                            setUserMenuOpen(false);
                            setUserFlyoutOpen(false);
                        }
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        margin: 0,
                        padding: 0,
                        height: 58,
                        width: 180,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        cursor: 'pointer',
                        zIndex: 120
                    }}
                >
                    <CapTracLogo width={180} height={58} />
                </button>
            )}
            {/* Flyout mobile menu */}
            {isMobile && burgerOpen && (
                <div className={`burger-flyout-menu animate-flyout${flyoutClosing ? ' closing' : ''}`}>
                    <ul className="navbar-menu mobile">
                        {renderMenu({
                            items: MENU_STRUCTURE.main,
                            mobile: true,
                            onDropdown: handleDropdown,
                            onDropdownLeave: handleDropdownLeave
                        })}
                        <UserMenu
                            mobile
                            userName={userDisplayName}
                            onMobileToggle={() => setUserFlyoutOpen(prev => !prev)}
                        />
                    </ul>
                    {renderMobileFlyoutSubmenu()}
                    {userFlyoutOpen && renderMobileUserFlyout()}
                </div>
            )}
        </nav>
    );
};

export default NavBar;